import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6slg0id1d31f3q961sg';

export default function BookingSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setError('Network error. Please call (267) 773-1129 to book.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-28 bg-[#080808] relative">
      <div className="max-w-3xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-14">
          <p className="text-gray-500 text-xs tracking-[0.35em] uppercase mb-4">Secure Your Spot</p>
          <h2 className="font-display silver-gradient-text text-5xl md:text-6xl font-semibold tracking-wide mb-6">
            Book Now
          </h2>
          <p className="text-gray-400 text-sm tracking-wide leading-relaxed">
            Fill in the form below to reserve your luxury prom chauffeur experience in Philadelphia.
            A <strong className="text-gray-300">$500 non-refundable deposit</strong> is required to confirm your booking.
          </p>
        </div>

        {submitted ? (
          <div className="card-luxury rounded-2xl p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center silver-border rounded-full mx-auto mb-6">
              <i className="ri-check-line text-2xl text-gray-300"></i>
            </div>
            <h3 className="font-display silver-gradient-text text-3xl font-semibold mb-3">Request Received!</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Thank you for your booking request! We'll contact you within 24 hours to confirm your Philadelphia prom date and send payment details for your $500 deposit.
            </p>
            <p className="text-gray-500 text-xs">
              Questions? Call us at <a href="tel:2677731129" className="text-gray-300 hover:text-white cursor-pointer">(267) 773-1129</a>
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-6 btn-outline-silver rounded-full px-8 py-3 text-xs tracking-[0.15em] uppercase cursor-pointer whitespace-nowrap"
            >
              Submit Another
            </button>
          </div>
        ) : (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="card-luxury rounded-2xl p-8 md:p-10 space-y-5"
          >
            {/* Row: Name + Phone */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Full Name *</label>
                <input
                  type="text"
                  name="full_name"
                  required
                  placeholder="Your full name"
                  className="luxury-input w-full rounded-lg px-4 py-3"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="(215) 000-0000"
                  className="luxury-input w-full rounded-lg px-4 py-3"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                className="luxury-input w-full rounded-lg px-4 py-3"
              />
            </div>

            {/* Row: Vehicle + Service */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Select Vehicle *</label>
                <select name="vehicle" required className="luxury-input w-full rounded-lg px-4 py-3 cursor-pointer">
                  <option value="">Choose vehicle...</option>
                  <option value="2026 BMW 740i - Blue">2026 BMW 740i (Midnight Blue)</option>
                  <option value="2026 BMW 530i - White">2026 BMW 530i (Pearl White)</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Service Type *</label>
                <select name="service_type" required className="luxury-input w-full rounded-lg px-4 py-3 cursor-pointer">
                  <option value="">Choose service...</option>
                  <option value="Drop-Off Only">Drop-Off Only</option>
                  <option value="Round Trip">Round Trip</option>
                </select>
              </div>
            </div>

            {/* Prom Date */}
            <div>
              <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Prom Date *</label>
              <input
                type="date"
                name="prom_date"
                required
                className="luxury-input w-full rounded-lg px-4 py-3 cursor-pointer"
              />
            </div>

            {/* Row: Pickup + Venue */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Pickup Address *</label>
                <input
                  type="text"
                  name="pickup_address"
                  required
                  placeholder="Philadelphia, PA address"
                  className="luxury-input w-full rounded-lg px-4 py-3"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Prom Venue *</label>
                <input
                  type="text"
                  name="prom_venue"
                  required
                  placeholder="Venue name or address"
                  className="luxury-input w-full rounded-lg px-4 py-3"
                />
              </div>
            </div>

            {/* Passengers */}
            <div>
              <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Number of Passengers *</label>
              <select name="passengers" required className="luxury-input w-full rounded-lg px-4 py-3 cursor-pointer">
                <option value="">Select passengers...</option>
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>{n} passenger{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>

            {/* Special Requests */}
            <div>
              <label className="block text-gray-400 text-xs tracking-[0.15em] uppercase mb-2">Special Requests</label>
              <textarea
                name="special_requests"
                rows={3}
                maxLength={500}
                placeholder="Any special requests or notes... (max 500 characters)"
                className="luxury-input w-full rounded-lg px-4 py-3 resize-none"
              ></textarea>
            </div>

            {error && (
              <p className="text-red-400 text-xs text-center">{error}</p>
            )}

            {/* Deposit Note */}
            <div className="bg-black/40 border border-gray-800 rounded-xl p-4 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-gray-500 text-sm"></i>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                A <strong className="text-gray-400">$500 non-refundable deposit</strong> is required to confirm your booking. After submitting this form, we'll reach out with payment instructions.
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-silver rounded-xl py-4 text-xs tracking-[0.2em] uppercase cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Booking Request'}
            </button>
          </form>
        )}
      </div>

      <div className="section-divider mt-20"></div>
    </section>
  );
}
